import { useLocation } from "react-router-dom";
import { useQuery } from "@origin-digital/query";
import { ICategoryDto } from "../contract";
import { fetchCategories } from "../services/categories";
import { isLandingPath } from "../helpers/routes";
import { toError, useShouldThrowQueryError } from "./useShouldThrowQueryError";

export interface UseCategoriesResult {
  categories: ICategoryDto[];
  loading: boolean;
  error: Error | null;
}

const MIN_LANDING_CATEGORY_COUNT = 8;

const assertLandingCategoryMinimum = (
  pathname: string,
  categories: ICategoryDto[],
  isLoading: boolean,
  error: Error | null
): void => {
  const isLandingPage = isLandingPath(pathname);

  if (
    !isLandingPage ||
    isLoading ||
    error ||
    categories.length >= MIN_LANDING_CATEGORY_COUNT
  ) {
    return;
  }

  throw new Error(
    `Expected at least ${MIN_LANDING_CATEGORY_COUNT} categories for the landing page, but received ${categories.length}`
  );
};

export const useCategories = (): UseCategoriesResult => {
  const { pathname } = useLocation();
  const { data, error, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => fetchCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes - categories change infrequently
  });

  const categoryError = toError(error);
  useShouldThrowQueryError(categoryError, {
    component: "useCategories",
    action: "fetchCategories",
  });

  assertLandingCategoryMinimum(pathname, data || [], isLoading, categoryError);

  return {
    categories: data || [],
    loading: isLoading,
    error: categoryError,
  };
};
